import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import {
  EMAIL_CREDIT_ROLLOVER_CAP_DEFAULT,
  EMAIL_CREDIT_ROLLOVER_CAP_VA,
  EMAIL_CREDIT_ROLLOVER_MONTHLY_DEFAULT,
  EMAIL_CREDIT_ROLLOVER_MONTHLY_VA,
  SubscriptionContext,
} from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { SubscriptionPortalFlow } from "schemas/dashboard";
import { Plans, isQuarterlyPlan } from "schemas/payments";
import { LIGHT_GREY } from "styles/oneOffColor";

import { useCheckout } from "components/StripeCheckout/useCheckout";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import { CancelScreen } from "./schema";
import styles from "./styles";

let LOSS_ITEMS = [
  {
    icon: "fa-regular fa-address-book",
    text: (
      <>
        <strong>400,000+ exclusive brand contacts</strong> available to paid
        subscribers only
      </>
    ),
  },
  {
    icon: "fa-regular fa-paper-plane",
    text: (
      <>
        <strong>Campaign pitching</strong> and <strong>Local Pitching</strong>{" "}
        opportunities
      </>
    ),
  },
  {
    icon: "fa-regular fa-envelope-open-text",
    text: (
      <>
        <strong>Email tracking</strong> — you won't know when brands open your
        emails
      </>
    ),
  },
];

interface Props {
  open: boolean;
  closeDialog: () => void;
}

const CancelFlow = ({ open, closeDialog }: Props) => {
  const { getToken } = useAuth();
  const { plan, subscription, plans, isAtEmailCreditCap, isVaPlan } =
    useContext(SubscriptionContext);
  const { handleManagePlan } = useSubscription();
  const isQuarterly = isQuarterlyPlan(plan);

  const triggerCancel = async () => {
    trackEvent("Subscription - Cancel Plan Initiated", { plan: plan! });
    handleManagePlan(plan!, SubscriptionPortalFlow.subscription_cancel);
  };
  const { currentOrg, organizationProProfile } = useContext(
    OrganizationUserContext,
  );
  const { setErrorAlert } = useContext(AlertContext);
  const { getCurrentEmailPackages } = useCheckout();

  const DialogTitles = {
    [CancelScreen.question]: <>Before you go...</>,
    [CancelScreen.gifted_credits]: <>One last gift for you! </>,
    [CancelScreen.gifted_credits_granted]: <>Credits applied! 🎉</>,
  };

  const [screen, setScreen] = useState<CancelScreen>(CancelScreen.question);
  const [hasGiftedCredits, setHasGiftedCredits] = useState(false);
  const [transactionsInCents, setTransactionsInCents] = useState(0);
  const [mainLoading, setMainLoading] = useState(false);

  const activeMonth = Number(subscription?.subscription?.totalMonthsSince || 0);
  const successMonth = Math.max(activeMonth + 1, 2);

  const totalEmails = plans?.[Plans.PRO]?.monthly_limit || 0;
  const grandfatheredLimit = organizationProProfile?.planConfig?.monthlyLimit;
  const hasGrandfatheredEmails =
    grandfatheredLimit != null && grandfatheredLimit > totalEmails;
  const extraEmails = hasGrandfatheredEmails
    ? grandfatheredLimit! - totalEmails
    : 0;

  const hasEmailVolumes = getCurrentEmailPackages() > 0;
  const emailItems = {
    icon: "fa-regular fa-paper-plane",
    text: (
      <>
        <strong>{50 * getCurrentEmailPackages()} bonus emails </strong> from
        your email volume add-on
      </>
    ),
  };

  const resetState = () => {
    setScreen(CancelScreen.question);
    closeDialog();
  };

  const verifiedIfUserHasReceivedCredits = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/user-subscription-feedbacks/paid`,
        "GET",
      );
      if (res.paid) {
        setHasGiftedCredits(true);
      }
    } catch (error) {
      // Do nothing
    }
  };

  const trackAnswer = async (isPrimaryButtonClicked: boolean) => {
    const buttonText = isPrimaryButtonClicked
      ? primaryTexts[screen]
      : secondaryTexts[screen];

    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/user-subscription-feedbacks/track`,
        "POST",
        {},
        { buttonText, screen },
      );
    } catch (error) {
      // Do nothing
    }
  };

  const grantCredits = async () => {
    setMainLoading(true);
    try {
      const { grantedCreditsInCents } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/user-subscription-feedbacks/grant-credits`,
        "POST",
      );
      if (!grantedCreditsInCents || grantedCreditsInCents <= 0) {
        resetState();
        return;
      }
      setTransactionsInCents(grantedCreditsInCents);
      setHasGiftedCredits(true);
      setScreen(CancelScreen.gifted_credits_granted);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setMainLoading(false);
    }
  };

  const nextPrimaryStep = {
    [CancelScreen.question]: resetState,
    [CancelScreen.gifted_credits]: grantCredits,
    [CancelScreen.gifted_credits_granted]: resetState,
  };

  const nextSecondaryStep = {
    [CancelScreen.question]: () => {
      if (!hasGiftedCredits) {
        setScreen(CancelScreen.gifted_credits);
      } else {
        triggerCancel();
      }
    },
    [CancelScreen.gifted_credits]: () => {
      triggerCancel();
    },
    [CancelScreen.gifted_credits_granted]: () => {
      handleManagePlan(plan!);
    },
  };

  const primaryTexts = {
    [CancelScreen.question]: "No, keep my access",
    [CancelScreen.gifted_credits]: "Accept This Offer",
    [CancelScreen.gifted_credits_granted]: "Close",
  };

  const secondaryTexts = {
    [CancelScreen.question]: "Yes, continue to cancel",
    [CancelScreen.gifted_credits]: "Cancel Subscription Anyway",
    [CancelScreen.gifted_credits_granted]: "View Next Invoice",
  };

  const primaryText = primaryTexts[screen];
  const secondaryText = secondaryTexts[screen];

  useEffect(() => {
    if (currentOrg?.id) {
      verifiedIfUserHasReceivedCredits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return (
    <Dialog
      maxWidth="sm"
      open={open}
      onClose={resetState}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 4,
          backgroundColor: LIGHT_GREY,
        },
      }}
    >
      <IconButton sx={styles.closeButton} onClick={resetState}>
        <Box component="i" className="fa-regular fa-xmark" />
      </IconButton>

      <DialogContent sx={{ mt: 3, px: { xs: 2, md: 6 } }}>
        <Typography variant="h4" textAlign="center" sx={{ mb: 3 }}>
          {DialogTitles[screen]}
        </Typography>

        {screen === CancelScreen.question && (
          <>
            <Typography
              sx={{
                fontSize: 14,
                color: "text.secondary",
                textAlign: "center",
                mb: 2,
              }}
            >
              We'd hate to see you leave. Here's what you should know about your
              account.
            </Typography>

            {isQuarterly && (
              <Typography
                sx={{
                  fontSize: 13,
                  color: "text.secondary",
                  textAlign: "center",
                  mb: 2,
                }}
              >
                You're on a quarterly plan. You'll keep full access through the
                end of your prepaid 3-month term — cancelling just stops it from
                renewing.
              </Typography>
            )}

            {!isAtEmailCreditCap &&
              (subscription?.rolloverEmailCredits || 0) > 0 && (
                <Box
                  sx={{
                    backgroundColor: "primary.main",
                    borderRadius: 3,
                    p: 2.5,
                    mb: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      mb: 1,
                    }}
                  >
                    <Box
                      component="i"
                      className="fa-solid fa-arrow-trend-up"
                      sx={{ fontSize: 14, color: "yellow.main" }}
                    />
                    <Typography
                      sx={{ color: "white", fontWeight: 700, fontSize: 15 }}
                    >
                      You're Building Up Credits
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.8)",
                      fontSize: 13,
                      mb: 1.5,
                    }}
                  >
                    Your unused credits roll over each month. Even if you pause
                    outreach, you're not losing credits — you're banking them
                    for later.
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.12)",
                      borderRadius: 2,
                      p: 1.5,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        mb: 1,
                      }}
                    >
                      <Typography
                        sx={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }}
                      >
                        Currently rolled over
                      </Typography>
                      <Typography
                        sx={{
                          color: "yellow.main",
                          fontSize: 17,
                          fontWeight: 800,
                        }}
                      >
                        {subscription!.rolloverEmailCredits!.toLocaleString()}{" "}
                        emails
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.7)",
                        fontSize: 11,
                        mb: 0.75,
                      }}
                    >
                      Next month you'll have →
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: "rgba(251,191,36,0.1)",
                        border: "1px solid rgba(251,191,36,0.5)",
                        borderRadius: 1.5,
                        p: 1.25,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "rgba(255,255,255,0.7)",
                          fontSize: 11,
                          mb: 0.5,
                        }}
                      >
                        Total credits available
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "yellow.main",
                            fontWeight: 800,
                            fontSize: 20,
                          }}
                        >
                          {(subscription?.monthlyLimit || 0).toLocaleString()}{" "}
                          emails
                        </Typography>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography
                            sx={{
                              color: "rgba(255,255,255,0.7)",
                              fontSize: 11,
                            }}
                          >
                            {subscription!.rolloverEmailCredits!.toLocaleString()}{" "}
                            rolled over
                          </Typography>
                          <Typography
                            sx={{
                              color: "rgba(255,255,255,0.7)",
                              fontSize: 11,
                            }}
                          >
                            +{" "}
                            {(
                              (subscription?.monthlyLimit || 0) -
                              (subscription?.rolloverEmailCredits || 0)
                            ).toLocaleString()}{" "}
                            monthly
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Typography
                    sx={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }}
                  >
                    * Roll over up to{" "}
                    {isVaPlan
                      ? EMAIL_CREDIT_ROLLOVER_MONTHLY_VA
                      : EMAIL_CREDIT_ROLLOVER_MONTHLY_DEFAULT}{" "}
                    credits/month. Max{" "}
                    {(isVaPlan
                      ? EMAIL_CREDIT_ROLLOVER_CAP_VA
                      : EMAIL_CREDIT_ROLLOVER_CAP_DEFAULT
                    ).toLocaleString()}{" "}
                    total emails per month.
                  </Typography>
                </Box>
              )}

            <Typography
              sx={{ fontSize: 15, fontWeight: 700, color: "black", mb: 1.5 }}
            >
              By cancelling, you'll lose access to:
            </Typography>
            <List disablePadding sx={{ mb: 2 }}>
              <ListItem
                disableGutters
                alignItems="flex-start"
                sx={styles.itemStyling}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Box
                    component="i"
                    className="fa-solid fa-xmark"
                    sx={{ fontSize: 14, color: "#DC2626" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <strong>
                        {(
                          (subscription?.monthlyLimit || 0) -
                          (subscription?.rolloverEmailCredits || 0)
                        ).toLocaleString()}{" "}
                        monthly email credits
                      </strong>
                      {!isAtEmailCreditCap &&
                        (subscription?.rolloverEmailCredits || 0) > 0 && (
                          <> + rollover credits</>
                        )}
                    </>
                  }
                  primaryTypographyProps={{ fontSize: 14 }}
                />
              </ListItem>
              {hasEmailVolumes && (
                <ListItem
                  alignItems="flex-start"
                  disableGutters
                  sx={styles.boldItemStyling}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Box
                      component="i"
                      className="fa-solid fa-xmark"
                      sx={{ fontSize: 14, color: "#DC2626" }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={emailItems.text}
                    primaryTypographyProps={{ fontSize: 14 }}
                  />
                </ListItem>
              )}
              {LOSS_ITEMS.map((item, i) => (
                <ListItem
                  key={i}
                  disableGutters
                  alignItems="flex-start"
                  sx={styles.itemStyling}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Box
                      component="i"
                      className="fa-solid fa-xmark"
                      sx={{ fontSize: 14, color: "#DC2626" }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{ fontSize: 14 }}
                  />
                </ListItem>
              ))}
              {extraEmails > 0 && (
                <ListItem
                  disableGutters
                  alignItems="flex-start"
                  sx={styles.boldItemStyling}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Box
                      component="i"
                      className="fa-solid fa-xmark"
                      sx={{ fontSize: 16, color: "#B91C1C" }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <>
                        You will lose access to{" "}
                        <strong>
                          {extraEmails.toLocaleString()} extra emails/month
                        </strong>{" "}
                        included in your plan if you cancel. This is a
                        grandfathered benefit that is not available to new
                        subscribers.
                      </>
                    }
                    primaryTypographyProps={{ fontSize: 14 }}
                  />
                </ListItem>
              )}
            </List>

            {/* Downgrade note */}
            <Typography
              sx={{
                fontSize: 13,
                color: "black",
                textAlign: "center",
                mb: 3,
              }}
            >
              You'll be downgraded to the{" "}
              <strong>
                Free Plan ({plans && plans[Plans.FREE]?.monthly_limit} emails /
                month){" "}
              </strong>{" "}
              at the end of your current billing period.
            </Typography>
          </>
        )}

        {screen === CancelScreen.gifted_credits && (
          <>
            <Typography textAlign="center" sx={{ mb: 3, fontSize: 16 }}>
              Creators who stay active for{" "}
              <strong>{successMonth} months</strong> see much higher success
              than those who leave{" "}
              {activeMonth <= 0
                ? "within the first month"
                : `after ${activeMonth} month${activeMonth > 1 ? "s" : ""}`}
              .
            </Typography>
            <Typography sx={{ fontSize: 80 }} textAlign="center">
              🎁
            </Typography>
            <Typography textAlign="center" sx={{ fontSize: 16 }}>
              Want 50% off your next month to stay active?
            </Typography>
          </>
        )}

        {screen === CancelScreen.gifted_credits_granted && (
          <Typography textAlign="center" sx={{ mb: 3, fontSize: 16 }}>
            You've received $
            <Box component="span" sx={{ fontSize: 30, color: "purple.main" }}>
              {Number(transactionsInCents / 100).toFixed(2)}
            </Box>{" "}
            credits for your next month! 💰
            <br />
            <br />
            It has been auto-applied to your next billing cycle. To view your
            next invoice, you can click on the button below.
          </Typography>
        )}

        <Grid
          container
          justifyContent="center"
          alignItems="center"
          sx={{ mt: 3, mb: 1 }}
          flexDirection="column"
          gap={1}
        >
          {primaryText && (
            <LoadingButton
              sx={styles.primaryButton}
              variant="contained"
              disableElevation
              onClick={() => {
                trackAnswer(true);
                nextPrimaryStep[screen]();
              }}
              loading={mainLoading}
            >
              {primaryText}
            </LoadingButton>
          )}
          {secondaryText && secondaryText.length > 0 && (
            <Button
              sx={styles.secondaryButton}
              onClick={() => {
                trackAnswer(false);
                nextSecondaryStep[screen]();
              }}
            >
              {secondaryText}
            </Button>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default CancelFlow;
